import React from 'react';
import {
  AdvancedMarker,
  Map as GoogleMap,
  MapMouseEvent,
  Polyline,
} from '@vis.gl/react-google-maps';
import {
  googleMapsMapId,
  hasGoogleMapsApiKey,
} from '../../components/maps/GoogleMapsProvider';
import { createAdminRoute, updateAdminRoute } from '../../services/admin';

type StopDraft = {
  id: string;
  name: string;
  latitude: number;
  longitude: number;
  notes: string;
};

const TILE_SIZE = 256;
const DEFAULT_CENTER = { latitude: 6.5244, longitude: 3.3792 };
type Coordinate = typeof DEFAULT_CENTER;

export function RouteCreator({ initialData, onClose, onCreated, routeId }: { initialData?: Record<string, unknown>; onClose: () => void; onCreated: () => void; routeId?: string }) {
  const initial = readInitialRoute(initialData);
  const [routeCode, setRouteCode] = React.useState(initial.routeCode);
  const [name, setName] = React.useState(initial.name);
  const [origin, setOrigin] = React.useState(initial.origin);
  const [destination, setDestination] = React.useState(initial.destination);
  const [fleetId, setFleetId] = React.useState(initial.fleetId);
  const [stops, setStops] = React.useState<StopDraft[]>(initial.stops);
  const [center, setCenter] = React.useState(DEFAULT_CENTER);
  const [zoom, setZoom] = React.useState(12);
  const [originPoint, setOriginPoint] = React.useState<Coordinate | null>(initial.originPoint);
  const [destinationPoint, setDestinationPoint] = React.useState<Coordinate | null>(initial.destinationPoint);
  const [routePath, setRoutePath] = React.useState<Coordinate[]>([]);
  const [isFindingRoute, setIsFindingRoute] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [error, setError] = React.useState('');
  const [mapSize, setMapSize] = React.useState({ width: 768, height: 550 });
  const mapRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const map = mapRef.current;
    if (!map) return undefined;
    const updateSize = () => setMapSize({ width: map.clientWidth, height: map.clientHeight });
    updateSize();
    const observer = new ResizeObserver(updateSize);
    observer.observe(map);
    return () => observer.disconnect();
  }, []);

  const tiles = getTiles(center.latitude, center.longitude, zoom, mapSize.width, mapSize.height);

  const addStop = (event: React.MouseEvent<HTMLDivElement>) => {
    const bounds = event.currentTarget.getBoundingClientRect();
    const point = screenToCoordinate(event.clientX - bounds.left, event.clientY - bounds.top, center, zoom, bounds.width, bounds.height);
    setStops((current) => [...current, {
      id: `${Date.now()}-${current.length}`,
      name: `Stop ${current.length + 1}`,
      latitude: point.latitude,
      longitude: point.longitude,
      notes: '',
    }]);
  };

  const addGoogleStop = (event: MapMouseEvent) => {
    const point = event.detail.latLng;
    if (!point) return;
    setStops((current) => [...current, {
      id: `${Date.now()}-${current.length}`,
      name: `Stop ${current.length + 1}`,
      latitude: point.lat,
      longitude: point.lng,
      notes: '',
    }]);
  };

  const updateStop = (id: string, changes: Partial<StopDraft>) => {
    setStops((current) => current.map((stop) => stop.id === id ? { ...stop, ...changes } : stop));
  };

  const moveStop = (index: number, direction: -1 | 1) => {
    setStops((current) => {
      const target = index + direction;
      if (target < 0 || target >= current.length) return current;
      const next = [...current];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const findRoute = async () => {
    if (!origin.trim() || !destination.trim()) {
      setError('Enter both origin and destination before showing the route.');
      return;
    }
    setError('');
    setIsFindingRoute(true);
    try {
      const resolvedOrigin = await geocode(origin);
      const resolvedDestination = await geocode(destination, resolvedOrigin);
      const path = await getRoadRoute(resolvedOrigin, resolvedDestination);
      setOriginPoint(resolvedOrigin);
      setDestinationPoint(resolvedDestination);
      setRoutePath(path);
      const fitted = fitCoordinates(path.length ? path : [resolvedOrigin, resolvedDestination], mapSize.width, mapSize.height);
      setCenter(fitted.center);
      setZoom(fitted.zoom);
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Unable to find route');
    } finally {
      setIsFindingRoute(false);
    }
  };

  const routePolyline = routePath.map((point) => {
    const position = coordinateToScreen(point.latitude, point.longitude, center, zoom, mapSize.width, mapSize.height);
    return `${position.x},${position.y}`;
  }).join(' ');

  return (
    <div className="modal-backdrop route-creator-backdrop" role="presentation" onMouseDown={onClose}>
      <form
        aria-labelledby="create-route-title"
        aria-modal="true"
        className="route-creator-dialog"
        role="dialog"
        onMouseDown={(event) => event.stopPropagation()}
        onSubmit={async (event) => {
          event.preventDefault();
          setError('');
          setIsSubmitting(true);
          try {
            if (!originPoint || !destinationPoint) {
              throw new Error('Show the route before creating it so the origin and destination coordinates can be included.');
            }
            const submittedStops = [
              { name: origin.trim(), latitude: originPoint.latitude, longitude: originPoint.longitude, notes: 'Route origin' },
              ...stops.map((stop) => ({ name: stop.name.trim(), latitude: stop.latitude, longitude: stop.longitude, notes: stop.notes.trim() })),
              { name: destination.trim(), latitude: destinationPoint.latitude, longitude: destinationPoint.longitude, notes: 'Route destination' },
            ];
            const payload = {
              route_code: routeCode.trim(),
              name: name.trim(),
              origin_name: origin.trim(),
              destination_name: destination.trim(),
              ...(fleetId.trim() ? { fleet_id: fleetId.trim() } : {}),
              stops: submittedStops.map((stop, index) => ({
                sequence: index + 1,
                name: stop.name,
                latitude: stop.latitude,
                longitude: stop.longitude,
                notes: stop.notes,
              })),
              metadata: {},
            };
            if (routeId) await updateAdminRoute(routeId, payload);
            else await createAdminRoute(payload);
            onCreated();
          } catch (requestError) {
            setError(requestError instanceof Error ? requestError.message : `Unable to ${routeId ? 'update' : 'create'} route`);
          } finally {
            setIsSubmitting(false);
          }
        }}
      >
        <div className="route-creator-head">
          <div><h2 id="create-route-title">{routeId ? 'Edit route' : 'Create route'}</h2><span>Click the map to add stops in travel order.</span></div>
          <button className="btn-icon borderless" type="button" aria-label="Close route creator" onClick={onClose}><span aria-hidden="true">&times;</span></button>
        </div>

        {error ? <div className="kyc-message passengers-error" role="alert">{error}</div> : null}

        <div className="route-creator-layout">
          <div className="route-creator-fields">
            <div className="route-fields-grid">
              <label className="form-group"><span>Route code</span><input required value={routeCode} onChange={(event) => setRouteCode(event.target.value)} placeholder="e.g. LAG-LEK-01" /></label>
              <label className="form-group"><span>Route name</span><input required value={name} onChange={(event) => setName(event.target.value)} placeholder="e.g. Lekki Express" /></label>
              <label className="form-group"><span>Origin name</span><input required value={origin} onChange={(event) => setOrigin(event.target.value)} /></label>
              <label className="form-group"><span>Destination name</span><input required value={destination} onChange={(event) => setDestination(event.target.value)} /></label>
              <label className="form-group route-fleet-field"><span>Fleet ID <small>(optional)</small></span><input value={fleetId} onChange={(event) => setFleetId(event.target.value)} placeholder="Fleet UUID" /></label>
            </div>

            <div className="route-map-toolbar">
              <strong>Stop map</strong>
              <button className="btn btn-sm btn-outline" disabled={isFindingRoute} type="button" onClick={findRoute}>{isFindingRoute ? 'Finding route...' : 'Show route'}</button>
              <span>Enter origin and destination, then show the route.</span>
              <button className="btn-icon route-map-zoom" type="button" aria-label="Zoom out" onClick={() => setZoom((current) => Math.max(3, current - 1))}>−</button>
              <strong className="route-map-zoom-level">{zoom}</strong>
              <button className="btn-icon route-map-zoom" type="button" aria-label="Zoom in" onClick={() => setZoom((current) => Math.min(18, current + 1))}>+</button>
            </div>
            <div className="route-stop-map" ref={mapRef} onClick={hasGoogleMapsApiKey ? undefined : addStop}>
              {hasGoogleMapsApiKey ? (
                <GoogleMap
                  center={{ lat: center.latitude, lng: center.longitude }}
                  gestureHandling="greedy"
                  mapId={googleMapsMapId}
                  mapTypeControl={false}
                  onCameraChanged={(event) => {
                    setCenter({
                      latitude: event.detail.center.lat,
                      longitude: event.detail.center.lng,
                    });
                    setZoom(event.detail.zoom);
                  }}
                  onClick={addGoogleStop}
                  streetViewControl={false}
                  zoom={zoom}
                >
                  {routePath.length ? (
                    <Polyline
                      path={routePath.map((point) => ({ lat: point.latitude, lng: point.longitude }))}
                      strokeColor="#2d7dd2"
                      strokeOpacity={0.9}
                      strokeWeight={5}
                    />
                  ) : null}
                  {originPoint ? (
                    <AdvancedMarker position={{ lat: originPoint.latitude, lng: originPoint.longitude }} title="Route origin">
                      <span className="google-route-endpoint origin">O</span>
                    </AdvancedMarker>
                  ) : null}
                  {destinationPoint ? (
                    <AdvancedMarker position={{ lat: destinationPoint.latitude, lng: destinationPoint.longitude }} title="Route destination">
                      <span className="google-route-endpoint destination">D</span>
                    </AdvancedMarker>
                  ) : null}
                  {stops.map((stop, index) => (
                    <AdvancedMarker
                      key={stop.id}
                      onClick={() => setCenter({ latitude: stop.latitude, longitude: stop.longitude })}
                      position={{ lat: stop.latitude, lng: stop.longitude }}
                      title={stop.name}
                    >
                      <button aria-label={`Center map on ${stop.name}`} className="google-route-marker" type="button">
                        {index + 1}
                      </button>
                    </AdvancedMarker>
                  ))}
                </GoogleMap>
              ) : null}
              {!hasGoogleMapsApiKey ? <>
              {tiles.map((tile) => <img alt="" draggable={false} key={`${tile.x}-${tile.y}`} src={`https://tile.openstreetmap.org/${zoom}/${tile.x}/${tile.y}.png`} style={{ left: tile.left, top: tile.top }} />)}
              {routePolyline ? <svg className="route-map-path" viewBox={`0 0 ${mapSize.width} ${mapSize.height}`}><polyline points={routePolyline} /></svg> : null}
              {originPoint ? <MapEndpoint label="O" point={originPoint} center={center} zoom={zoom} size={mapSize} tone="origin" /> : null}
              {destinationPoint ? <MapEndpoint label="D" point={destinationPoint} center={center} zoom={zoom} size={mapSize} tone="destination" /> : null}
              {stops.map((stop, index) => {
                const position = coordinateToScreen(stop.latitude, stop.longitude, center, zoom, mapSize.width, mapSize.height);
                return <button aria-label={`Center map on ${stop.name}`} className="route-map-marker" key={stop.id} style={{ left: position.x, top: position.y }} type="button" onClick={(event) => { event.stopPropagation(); setCenter({ latitude: stop.latitude, longitude: stop.longitude }); }}>{index + 1}</button>;
              })}
              <span className="route-map-attribution">© OpenStreetMap contributors</span>
              </> : null}
            </div>
          </div>

          <div className="route-stops-panel">
            <div><h3>Ordered stops</h3><span>{stops.length} stop{stops.length === 1 ? '' : 's'}</span></div>
            <div className="route-stops-list">
              {stops.length ? stops.map((stop, index) => (
                <article className="route-stop-card" key={stop.id}>
                  <div className="route-stop-card-head">
                    <strong><span>{index + 1}</span>{stop.name}</strong>
                    <div>
                      <button disabled={index === 0} type="button" onClick={() => moveStop(index, -1)} aria-label="Move stop up">↑</button>
                      <button disabled={index === stops.length - 1} type="button" onClick={() => moveStop(index, 1)} aria-label="Move stop down">↓</button>
                      <button type="button" onClick={() => setStops((current) => current.filter((item) => item.id !== stop.id))} aria-label="Remove stop">&times;</button>
                    </div>
                  </div>
                  <input aria-label={`Stop ${index + 1} name`} required value={stop.name} onChange={(event) => updateStop(stop.id, { name: event.target.value })} />
                  <div className="route-stop-coordinates">
                    <input aria-label={`Stop ${index + 1} latitude`} type="number" min="-90" max="90" step="any" required value={stop.latitude} onChange={(event) => updateStop(stop.id, { latitude: Number(event.target.value) })} />
                    <input aria-label={`Stop ${index + 1} longitude`} type="number" min="-180" max="180" step="any" required value={stop.longitude} onChange={(event) => updateStop(stop.id, { longitude: Number(event.target.value) })} />
                  </div>
                  <textarea aria-label={`Stop ${index + 1} notes`} placeholder="Notes (optional)" value={stop.notes} onChange={(event) => updateStop(stop.id, { notes: event.target.value })} />
                </article>
              )) : <div className="route-stops-empty">Click anywhere on the map to add the first stop.</div>}
            </div>
          </div>
        </div>

        <div className="route-creator-actions">
          <button className="btn" type="button" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" disabled={isSubmitting || !originPoint || !destinationPoint} type="submit">{isSubmitting ? `${routeId ? 'Updating' : 'Creating'} route...` : `${routeId ? 'Update' : 'Create'} route`}</button>
        </div>
      </form>
    </div>
  );
}

function readInitialRoute(data?: Record<string, unknown>) {
  const empty = { routeCode: '', name: '', origin: '', destination: '', fleetId: '', stops: [] as StopDraft[], originPoint: null as Coordinate | null, destinationPoint: null as Coordinate | null };
  if (!data) return empty;
  const source = unwrapRoute(data);
  const rawStops = Array.isArray(source.stops) ? source.stops.filter(isObject) : [];
  const parsedStops = rawStops.map((stop, index) => ({
    id: String(readValue(stop, 'id', 'stop_id') ?? `existing-${index}`),
    name: String(readValue(stop, 'name', 'stop_name') ?? `Stop ${index + 1}`),
    latitude: Number(readValue(stop, 'latitude', 'lat') ?? 0),
    longitude: Number(readValue(stop, 'longitude', 'lng', 'lon') ?? 0),
    notes: String(readValue(stop, 'notes') ?? ''),
  }));
  const first = parsedStops[0];
  const last = parsedStops[parsedStops.length - 1];
  return {
    routeCode: String(readValue(source, 'route_code', 'code') ?? ''),
    name: String(readValue(source, 'name', 'route_name') ?? ''),
    origin: String(readValue(source, 'origin_name', 'origin') ?? first?.name ?? ''),
    destination: String(readValue(source, 'destination_name', 'destination') ?? last?.name ?? ''),
    fleetId: String(readValue(source, 'fleet_id') ?? ''),
    stops: parsedStops.length > 2 ? parsedStops.slice(1, -1) : [],
    originPoint: first ? { latitude: first.latitude, longitude: first.longitude } : null,
    destinationPoint: last ? { latitude: last.latitude, longitude: last.longitude } : null,
  };
}

function unwrapRoute(data: Record<string, unknown>): Record<string, unknown> {
  for (const key of ['route', 'data', 'result', 'payload']) {
    if (isObject(data[key])) return unwrapRoute(data[key] as Record<string, unknown>);
  }
  return data;
}

function readValue(source: Record<string, unknown>, ...keys: string[]) {
  for (const key of keys) {
    const value = source[key];
    if (typeof value === 'string' || typeof value === 'number') return value;
  }
  return undefined;
}

function isObject(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function MapEndpoint({ center, label, point, size, tone, zoom }: { center: Coordinate; label: string; point: Coordinate; size: { width: number; height: number }; tone: string; zoom: number }) {
  const position = coordinateToScreen(point.latitude, point.longitude, center, zoom, size.width, size.height);
  return <span className={`route-map-endpoint ${tone}`} style={{ left: position.x, top: position.y }}>{label}</span>;
}

async function geocode(query: string, near?: Coordinate): Promise<Coordinate> {
  const params = new URLSearchParams({ format: 'jsonv2', limit: '5', q: query });
  if (near) {
    const longitudeSpan = 5;
    const latitudeSpan = 5;
    params.set('viewbox', [
      near.longitude - longitudeSpan,
      near.latitude + latitudeSpan,
      near.longitude + longitudeSpan,
      near.latitude - latitudeSpan,
    ].join(','));
    params.set('bounded', '1');
  }
  const response = await fetch(`https://nominatim.openstreetmap.org/search?${params.toString()}`, { headers: { Accept: 'application/json' } });
  if (!response.ok) throw new Error(`Unable to locate "${query}".`);
  const results = await response.json() as { lat?: string; lon?: string }[];
  const result = results[0];
  if (!result?.lat || !result.lon) {
    throw new Error(near
      ? `No nearby map location found for "${query}". Add a city or state to make it more specific.`
      : `No map location found for "${query}".`);
  }
  return { latitude: Number(result.lat), longitude: Number(result.lon) };
}

async function getRoadRoute(origin: Coordinate, destination: Coordinate): Promise<Coordinate[]> {
  const coordinates = `${origin.longitude},${origin.latitude};${destination.longitude},${destination.latitude}`;
  const response = await fetch(`https://router.project-osrm.org/route/v1/driving/${coordinates}?overview=full&geometries=geojson`);
  if (!response.ok) {
    const result = await response.json().catch(() => undefined) as { message?: string } | undefined;
    throw new Error(result?.message === 'Impossible route between points'
      ? 'No drivable route exists between the selected origin and destination. Make the destination more specific.'
      : result?.message || 'Unable to calculate the road route.');
  }
  const result = await response.json() as { routes?: { geometry?: { coordinates?: number[][] } }[] };
  const points = result.routes?.[0]?.geometry?.coordinates;
  if (!points?.length) throw new Error('No road route was found between these locations.');
  return points.map(([longitude, latitude]) => ({ latitude, longitude }));
}

function fitCoordinates(points: Coordinate[], width: number, height: number) {
  const minLatitude = Math.min(...points.map((point) => point.latitude));
  const maxLatitude = Math.max(...points.map((point) => point.latitude));
  const minLongitude = Math.min(...points.map((point) => point.longitude));
  const maxLongitude = Math.max(...points.map((point) => point.longitude));
  const center = { latitude: (minLatitude + maxLatitude) / 2, longitude: (minLongitude + maxLongitude) / 2 };
  for (let zoom = 17; zoom >= 3; zoom -= 1) {
    const topLeft = coordinateToWorld(maxLatitude, minLongitude, zoom);
    const bottomRight = coordinateToWorld(minLatitude, maxLongitude, zoom);
    if (bottomRight.x - topLeft.x <= width - 100 && bottomRight.y - topLeft.y <= height - 100) return { center, zoom };
  }
  return { center, zoom: 3 };
}

function getTiles(latitude: number, longitude: number, zoom: number, width: number, height: number) {
  const center = coordinateToWorld(latitude, longitude, zoom);
  const startX = Math.floor((center.x - width / 2) / TILE_SIZE);
  const startY = Math.floor((center.y - height / 2) / TILE_SIZE);
  const endX = Math.floor((center.x + width / 2) / TILE_SIZE);
  const endY = Math.floor((center.y + height / 2) / TILE_SIZE);
  const tiles = [];
  for (let y = startY; y <= endY; y += 1) {
    for (let x = startX; x <= endX; x += 1) {
      tiles.push({ x, y, left: x * TILE_SIZE - (center.x - width / 2), top: y * TILE_SIZE - (center.y - height / 2) });
    }
  }
  return tiles;
}

function coordinateToWorld(latitude: number, longitude: number, zoom: number) {
  const scale = TILE_SIZE * (2 ** zoom);
  const sin = Math.sin(Math.max(-85.0511, Math.min(85.0511, latitude)) * Math.PI / 180);
  return { x: (longitude + 180) / 360 * scale, y: (0.5 - Math.log((1 + sin) / (1 - sin)) / (4 * Math.PI)) * scale };
}

function worldToCoordinate(x: number, y: number, zoom: number) {
  const scale = TILE_SIZE * (2 ** zoom);
  const longitude = x / scale * 360 - 180;
  const n = Math.PI - 2 * Math.PI * y / scale;
  return { latitude: 180 / Math.PI * Math.atan(0.5 * (Math.exp(n) - Math.exp(-n))), longitude };
}

function screenToCoordinate(x: number, y: number, center: typeof DEFAULT_CENTER, zoom: number, width: number, height: number) {
  const world = coordinateToWorld(center.latitude, center.longitude, zoom);
  return worldToCoordinate(world.x + x - width / 2, world.y + y - height / 2, zoom);
}

function coordinateToScreen(latitude: number, longitude: number, center: typeof DEFAULT_CENTER, zoom: number, width: number, height: number) {
  const world = coordinateToWorld(latitude, longitude, zoom);
  const centerWorld = coordinateToWorld(center.latitude, center.longitude, zoom);
  return { x: world.x - centerWorld.x + width / 2, y: world.y - centerWorld.y + height / 2 };
}
